import { ToiStatusOption } from '../models/controle-toi.model';
import { PdfToiEventoItem } from '../models/pdf-toi.model';

export const STATUS_AGUARDANDO_VALIDACAO = 'AGUARDANDO_VALIDACAO';

/** Status TOI — IDs alinhados à tabela de domínio do backend. */
export const TOI_STATUS_OPTIONS: ToiStatusOption[] = [
  { label: 'TOI Gerado', value: 'TOI Gerado', idAtividadeStatusToi: 1, sigla: 'TOI_GERADO' },
  { label: 'Entregue em mãos', value: 'Entregue em mãos', idAtividadeStatusToi: 2, sigla: 'TOI_ENTREGUE_MAOS' },
  {
    label: 'Aguardando validação',
    value: 'Aguardando validação',
    idAtividadeStatusToi: 3,
    sigla: 'AGUARDANDO_VALIDACAO',
  },
  { label: 'Reprovado', value: 'Reprovado', idAtividadeStatusToi: 4, sigla: 'REPROVADO' },
  { label: 'Aprovado', value: 'Aprovado', idAtividadeStatusToi: 5, sigla: 'APROVADO' },
  {
    label: 'Aguardando envio Correios',
    value: 'Aguardando envio Correios',
    idAtividadeStatusToi: 6,
    sigla: 'AGUARDANDO_ENVIO_CORREIOS',
  },
  {
    label: 'Pendente confirmação para envio',
    value: 'Pendente confirmação para envio',
    idAtividadeStatusToi: 7,
    sigla: 'PENDENTE_CONFIRMACAO_ENVIO',
  },
  {
    label: 'Aguardando tratamento envio',
    value: 'Aguardando tratamento envio',
    idAtividadeStatusToi: 8,
    sigla: 'AGUARDANDO_TRATAMENTO_ENVIO',
  },
  { label: 'TOI tratado', value: 'TOI tratado', idAtividadeStatusToi: 9, sigla: 'TOI_TRATADO' },
  {
    label: 'Aguardando retorno AR',
    value: 'Aguardando retorno AR',
    idAtividadeStatusToi: 10,
    sigla: 'AGUARDANDO_RETORNO_AR',
  },
  {
    label: 'Aguardando detalhes de entrega',
    value: 'Aguardando detalhes de entrega',
    idAtividadeStatusToi: 11,
    sigla: 'AGUARDANDO_DETALHES_ENTREGA',
  },
  {
    label: 'Tentativa de entrega 1',
    value: 'Tentativa de entrega 1',
    idAtividadeStatusToi: 12,
    sigla: 'TENTATIVA_ENTREGA_1',
  },
  {
    label: 'Tentativa de entrega 2',
    value: 'Tentativa de entrega 2',
    idAtividadeStatusToi: 13,
    sigla: 'TENTATIVA_ENTREGA_2',
  },
  {
    label: 'Tentativa de entrega 3',
    value: 'Tentativa de entrega 3',
    idAtividadeStatusToi: 14,
    sigla: 'TENTATIVA_ENTREGA_3',
  },
  { label: 'Entregue', value: 'Entregue', idAtividadeStatusToi: 15, sigla: 'ENTREGUE' },
];

const TOI_STATUS_SIGLA_MAP = Object.fromEntries(
  TOI_STATUS_OPTIONS.filter((o) => o.sigla).map((o) => [o.sigla!, o.value])
) as Record<string, string>;

/** Rótulos legados / variações da API mapeados para o valor canônico. */
const TOI_STATUS_API_ALIASES: Record<string, string> = {
  ...TOI_STATUS_SIGLA_MAP,
  Gerado: 'TOI Gerado',
  'TOI GERADO': 'TOI Gerado',
  Pendente: 'TOI Gerado',
  'Aguardando Validação': 'Aguardando validação',
  'Recusa de recebimento': 'Aguardando validação',
  'Aguardando Envio Correios': 'Aguardando envio Correios',
  'Envio pelos Correios': 'Aguardando envio Correios',
  'Pendente Confirmação para Envio': 'Pendente confirmação para envio',
  'Tentativa de Entrega 1': 'Tentativa de entrega 1',
  'Tentativa de Entrega 2': 'Tentativa de entrega 2',
  'Tentativa de Entrega 3': 'Tentativa de entrega 3',
};

function findToiStatusOptionByLabel(status: string): ToiStatusOption | undefined {
  return TOI_STATUS_OPTIONS.find(
    (option) => option.value.localeCompare(status, 'pt-BR', { sensitivity: 'accent' }) === 0
  );
}

export function normalizeToiStatusLabel(status?: string | null): string | undefined {
  const trimmed = status?.trim();
  if (!trimmed) {
    return undefined;
  }

  const direct = findToiStatusOptionByLabel(trimmed);
  if (direct) {
    return direct.value;
  }

  const alias =
    TOI_STATUS_API_ALIASES[trimmed] ??
    TOI_STATUS_API_ALIASES[trimmed.toUpperCase()] ??
    Object.entries(TOI_STATUS_API_ALIASES).find(
      ([key]) => key.localeCompare(trimmed, 'pt-BR', { sensitivity: 'accent' }) === 0
    )?.[1];

  if (alias) {
    return alias;
  }

  return trimmed;
}

export function isAguardandoValidacaoStatus(status?: string | null): boolean {
  return normalizeToiStatusLabel(status) === 'Aguardando validação';
}

export function resolveIdAtividadeStatusToi(eventosToi?: string | null): number | undefined {
  const status = normalizeToiStatusLabel(eventosToi);
  if (!status) {
    return undefined;
  }

  return findToiStatusOptionByLabel(status)?.idAtividadeStatusToi;
}

export function resolveToiStatusLabelById(idAtividadeStatusToi?: number | null): string | undefined {
  if (idAtividadeStatusToi == null || idAtividadeStatusToi <= 0) {
    return undefined;
  }

  return TOI_STATUS_OPTIONS.find((option) => option.idAtividadeStatusToi === idAtividadeStatusToi)?.value;
}

export function resolveToiStatusSeverity(
  status: string
): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' {
  const normalized = normalizeToiStatusLabel(status) ?? status;

  switch (normalized) {
    case 'Entregue':
    case 'Entregue em mãos':
    case 'Aprovado':
    case 'TOI tratado':
      return 'success';
    case 'Reprovado':
      return 'danger';
    case 'Aguardando tratamento envio':
    case 'Pendente confirmação para envio':
    case 'Tentativa de entrega 1':
    case 'Tentativa de entrega 2':
    case 'Tentativa de entrega 3':
      return 'warn';
    case 'Aguardando validação':
    case 'Aguardando envio Correios':
    case 'Aguardando retorno AR':
    case 'Aguardando detalhes de entrega':
    case 'TOI Gerado':
      return 'info';
    default:
      return 'secondary';
  }
}

/** Status em que o TOI não permite mais edição (somente consulta). */
export const TOI_STATUS_SOMENTE_LEITURA = new Set<string>([
  'Entregue em mãos',
  'Reprovado',
  'Entregue',
]);

export function isToiStatusSomenteLeitura(status?: string | null): boolean {
  const normalized = normalizeToiStatusLabel(status);
  return normalized != null && TOI_STATUS_SOMENTE_LEITURA.has(normalized);
}

const TOI_STATUS_COM_CONTAGEM_DIAS = new Set<string>(['Entregue em mãos', 'Entregue']);

export function isToiStatusComContagemDias(status?: string | null): boolean {
  const normalized = normalizeToiStatusLabel(status);
  return normalized != null && TOI_STATUS_COM_CONTAGEM_DIAS.has(normalized);
}

export interface ToiStatusFlags {
  flagEntregueMaos: boolean;
  flagRecusaRecebimento: boolean;
  flagGerado: boolean;
  flagExigeEnvioCorreios: boolean;
  flagEnderecoPendenteTratamento?: boolean;
}

export function buildStatusToiLabel(flags: ToiStatusFlags): string {
  if (flags.flagEntregueMaos) return 'Entregue em mãos';
  if (flags.flagEnderecoPendenteTratamento) return 'Aguardando tratamento envio';
  if (flags.flagRecusaRecebimento) return 'Aguardando validação';
  if (flags.flagExigeEnvioCorreios) return 'Aguardando envio Correios';
  if (flags.flagGerado) return 'TOI Gerado';
  return 'TOI Gerado';
}

/** Status atual do TOI a partir do histórico de eventos (primeiro registro). */
export function resolveStatusToiFromEventos(
  eventosToi: PdfToiEventoItem[] | undefined | null,
  flags?: ToiStatusFlags
): string {
  const status = eventosToi?.[0]?.status?.trim();
  if (status) {
    return status;
  }
  if (flags) {
    return buildStatusToiLabel(flags);
  }
  return 'TOI Gerado';
}
